// src/dashboard/pages/ListAdminBookings/index.jsx
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import AdminBookingDetailsModal from '../../components/AdminBookingDetailsModal'

const StatusBadge = ({ status }) => {
  const map = { pending: 'secondary', confirmed: 'success', rejected: 'danger' };
  return <span className={`badge bg-${map[status] || 'secondary'}`}>{status}</span>;
};

/* ----------- Barre de filtres (statut + recherche client) ----------- */
function BookingFilters({ status, setStatus, clientQuery, setClientQuery, counts }) {
  const Btn = ({ value, label }) => (
    <button
      type="button"
      className={`btn ${status === value ? 'btn-primary' : 'btn-outline-primary'}`}
      onClick={() => setStatus(value)}
    >
      {label}{' '}
      {counts?.[value] !== undefined && (
        <span className="badge text-bg-light ms-2">{counts[value]}</span>
      )}
    </button>
  );

  return (
    <div className="d-flex flex-column justify-content-between flex-lg-row gap-3 align-items-lg-center mb-3">
      <div className="btn-group" role="group" aria-label="Filtre statut">
        <Btn value="all" label={`Tous`} />
        <Btn value="confirmed" label="Confirmées" />
        <Btn value="pending" label="En cours" />
        <Btn value="rejected" label="Refusées" />
      </div>

      <div className="ms-lg-3" style={{ maxWidth: 420 }}>
        <div className="input-group">
          <span className="input-group-text">Client</span>
          <input
            type="text"
            className="form-control"
            placeholder="Nom ou email…"
            value={clientQuery}
            onChange={(e) => setClientQuery(e.target.value)}
          />
          {clientQuery && (
            <button
              className="btn btn-outline-secondary"
              onClick={() => setClientQuery('')}
              type="button"
            >
              X
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminBookings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected,  setSelected]  = useState(null); // 👈 nouveau

  // filtres
  const [status, setStatus] = useState('all'); // all | confirmed | pending | rejected
  const [clientQuery, setClientQuery] = useState('');

  const authHeader = () => {
    const raw = localStorage.getItem('token_b2b') || '';
    return raw.startsWith('Bearer ') ? raw : `Bearer ${raw}`;
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`/api/admin/bookings`, {
          headers: { Authorization: authHeader() },
        });
        const d = res.data;
        const items = Array.isArray(d?.bookings) ? d.bookings : Array.isArray(d) ? d : [];
        setRows(
          items.map((r) => ({
            ...r,
            _edit: {
              discountType: r.discountType || '',
              discountValue: r.discountValue ?? '',
              adminNote: r.adminNote || '',
            },
          })),
        );
      } catch (e) {
        console.error('admin bookings load error:', e?.response?.data || e);
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const updateRowLocal = (id, patch) =>
    setRows((prev) => prev.map((r) => (r._id === id ? { ...r, ...patch } : r)));

  const setEdit = (id, patch) =>
    setRows((prev) =>
      prev.map((r) => (r._id === id ? { ...r, _edit: { ...r._edit, ...patch } } : r)),
    );

  const saveDiscount = async (row) => {
    const { discountType, discountValue } = row._edit;
    try {
      const { data } = await axios.patch(
        `/api/admin/bookings/${row._id}/discount`,
        {
          discountType: discountType || null,
          discountValue: discountValue ? Number(discountValue) : 0,
        },
        { headers: { Authorization: authHeader() } },
      );

      updateRowLocal(row._id, {
        ...data,
        _edit: {
          discountType: data.discountType || '',
          discountValue: data.discountValue ?? '',
          adminNote: data.adminNote || '',
        },
      });
    } catch (e) {
      console.error('save discount error:', e?.response?.data || e);
      alert("Échec de l’enregistrement de la remise");
    }
  };

  const updateStatus = async (row, newStatus) => {
    try {
      const { data } = await axios.patch(
        `/api/admin/bookings/${row._id}/status`,
        { status: newStatus, adminNote: row._edit.adminNote },
        { headers: { Authorization: authHeader() } },
      );
      updateRowLocal(row._id, {
        ...data,
        _edit: {
          discountType: data.discountType || '',
          discountValue: data.discountValue ?? '',
          adminNote: data.adminNote || '',
        },
      });
    } catch (e) {
      console.error('update status error:', e?.response?.data || e);
      alert('Échec de la mise à jour du statut');
    }
  };

  // ----- Comptages par statut -----
  const counts = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.all += 1;
        if (r.status === 'confirmed') acc.confirmed += 1;
        else if (r.status === 'pending') acc.pending += 1;
        else if (r.status === 'rejected') acc.rejected += 1;
        return acc;
      },
      { all: 0, confirmed: 0, pending: 0, rejected: 0 },
    );
  }, [rows]);

  // ----- Filtrage local (statut + client) -----
  const filtered = useMemo(() => {
    const q = clientQuery.trim().toLowerCase();
    return rows.filter((r) => {
      const okStatus = status === 'all' ? true : r.status === status;
      if (!okStatus) return false;
      if (!q) return true;
      const name =
        (r.userId?.companyName ||
          r.user?.companyName ||
          '') +
        ' ' +
        (r.userId?.email || r.user?.email || '');
      return name.toLowerCase().includes(q);
    });
  }, [rows, status, clientQuery]);

  return (
    <>
    {/* <SEO title="User B2b" description="Utilisateurs B2B" /> */}
    <div className="py-4">
      <h2 className="mb-3">Réservations B2B</h2>

      {/* 🔎 Filtres toujours visibles */}
      <BookingFilters
        status={status}
        setStatus={setStatus}
        clientQuery={clientQuery}
        setClientQuery={setClientQuery}
        counts={counts}
      />

      {/* 🧾 Tableau + overlay de chargement */}
      <div className="position-relative" style={{ minHeight: 240 }}>
        {loading && (
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-white bg-opacity-75">
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }} />
          </div>
        )}

        <div className="table-responsive">
          <table className="table align-middle">
            <thead>
              <tr>
                <th>Client</th>
                <th>Produit</th>
                <th>Départ</th>
                <th>Sous-Total</th>
                <th>Remise B2B</th>
                <th>Total</th>
                <th>Statut</th>
                <th>Note admin</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {(loading ? rows : filtered).map((r) => {
                const pax = Number(r.adults || 0) + Number(r.children || 0);
const subtotal = Number(r.totalPrice || 0);
const finalTotal = Number(r.finalTotalPrice || subtotal);


                return (
                  <tr key={r._id}>
                    <td>
                      <div className="fw-semibold">
                        {r.userId?.companyName || r.user?.companyName || '—'}
                      </div>
                      <div className="text-muted small">{r.userId?.email || r.user?.email}</div>
                    </td>

                    <td>
                      <div className="fw-semibold">{r.productName || r.productId}</div>
                      <div className="text-muted small">
                        {r.destination?.city} / {r.destination?.country}
                      </div>
                    </td>

                    <td>
                      <div>{new Date(r.date).toLocaleDateString('fr-FR')}</div>
                      <div className="text-muted small">From: {r.from || '—'}</div>
                      <div className="text-muted small">
                        {r.adults} ad. / {r.children} enf.
                      </div>
                    </td>

                    <td>
                      <div className="fw-semibold">
    {subtotal.toFixed(2)} {r.currency}
  </div>
                    </td>
                     <td>
                      <div className="fw-semibold">
    {r.discountValue} %
  </div>
                    </td>
                    <td className="fw-bold">
  {finalTotal.toFixed(2)} {r.currency}
</td>

                    

                 

                    <td>
                      <StatusBadge status={r.status} />
                    </td>

                    <td style={{ minWidth: 180 }}>
                      <input
                        className="form-control"
                        placeholder="Note admin…"
                        value={r._edit.adminNote}
                        onChange={(e) => setEdit(r._id, { adminNote: e.target.value })}
                      />
                    </td>

<td>
  <div className="d-flex flex-column gap-2">
    <button
      type="button"
      className="btn btn-link p-0 text-decoration-underline text-start"
      onClick={() => setSelected(r)}
    >
      Détails
    </button>

    {r.status === 'confirmed' ? (
      <span className="text-success fw-semibold d-inline-flex align-items-center gap-2">
        <i className="bi bi-check-circle"></i> Confirmée
      </span>
    ) : (
      <div className="btn-group">
        <button
          className="btn btn-success"
          onClick={() => updateStatus(r, 'confirmed')}
        >
          Confirmer
        </button>
        <button
          className="btn btn-danger"
          onClick={() => updateStatus(r, 'rejected')}
        >
          Refuser
        </button>
      </div>
    )}
  </div>
</td>
                  </tr>
                );
              })}

              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan="9" className="text-center text-muted py-4">
                    Aucune réservation.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
    {selected && (
  <AdminBookingDetailsModal
    booking={selected}
    onClose={() => setSelected(null)}
  />
)}
    </>
  );
}
